import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

import { useHostedInWorkQueueDialog } from '~/components/workQueue/dialogHost';

/**
 * Space kept above a detail page's table, so scrolling to the bottom still leaves the app
 * bar, the tab strip and the last of the header on screen rather than running the table
 * under the top of the window.
 */
export const TOP_RESERVE = 200;

/** The tab panel's own padding below the grid. */
export const BOTTOM_GAP = 24;

/** Never collapse below this, however short the window is. */
const MIN_HEIGHT = 240;

const DetailTableHeightContext = createContext<number | undefined>(undefined);

const availableHeight = () => Math.max(MIN_HEIGHT, window.innerHeight - TOP_RESERVE - BOTTOM_GAP);

/**
 * Bounds the grids inside a detail page's tabs to the window less the reserve above them,
 * so the page scrolls the header away and stops with the tabs still showing, and each grid
 * scrolls inside itself from there.
 *
 * A context rather than a prop threaded through every tab and section: the reserve above
 * the grid is the same on all four detail pages, so a table can read its own height
 * wherever it sits. Where there is no provider — the Work Queue dialog — the tables
 * keep growing to fit their rows.
 */
export function DetailTableHeightProvider({ children }: { children: ReactNode }) {
  const inDialog = useHostedInWorkQueueDialog();
  const [height, setHeight] = useState(availableHeight);

  useEffect(() => {
    const measure = () => setHeight(availableHeight());
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <DetailTableHeightContext.Provider value={inDialog ? undefined : height}>
      {children}
    </DetailTableHeightContext.Provider>
  );
}

export const useDetailTableHeight = (): number | undefined => useContext(DetailTableHeightContext);
